import { useGenericAudioLevelAlert } from '@heidi/capture/useGenericAudioLevelAlert';
import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { Progress } from '@heidi/design-system/progress';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn, useIsMobile } from '@heidi/utils';
import { useAtomValue } from 'jotai';
import { noop } from 'lodash-es';
import { AlertTriangle, Square } from 'lucide-react';
import { memo, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getDictationBridgePort } from '../../../../dictation-bridge-ports';
import { DictationBubbleShell } from '../../../shared/components/dictation-bubble-shell';
import { DictationListeningContent } from '../../../shared/components/dictation-listening-content';
import { MaxDurationApproachingContent } from '../../../shared/components/max-duration-approaching-content';
import { dictationIntlMessages } from '../../../shared/dictation-intl-messages';
import { DICTATION_BUBBLE_Z_INDEX_CLASS } from '../../../shared/styles';
import { getDictationBubbleUiHost } from '../../host';
import { useDictationStore } from '../../store/useDictationStore';
import { bubbleEnterAnimation } from '../consts/animation';
import { isMaxDurationApproachingAtom } from './hooks/useMaxDuration';

const POST_SPEECH_SILENCE_MS = 10000; // 10 seconds

const StillDictatingContent = () => {
  const [progressValue, setProgressValue] = useState(100);

  // Progress bar countdown for "Still dictating?" state
  useEffect(() => {
    const startTime = performance.now();
    let rafId: number;

    const tick = () => {
      const elapsed = performance.now() - startTime;
      const remaining = Math.max(0, POST_SPEECH_SILENCE_MS - elapsed);
      const percentage = (remaining / POST_SPEECH_SILENCE_MS) * 100;
      setProgressValue(percentage);

      if (percentage > 0) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <>
      <HeidiLogoMark aria-label="Heidi" className="size-5" />
      <div className="flex flex-1 flex-col gap-0.5">
        <TypographyP2>
          <FormattedMessage {...dictationIntlMessages.stillDictatingTitle} />
        </TypographyP2>
        <TypographyCaption className="text-text-secondary">
          <FormattedMessage {...dictationIntlMessages.stillDictatingSubtitle} />
        </TypographyCaption>
      </div>
      <Progress
        value={progressValue}
        className="absolute bottom-0 left-0 h-1 w-full rounded-lg bg-background-secondary [&>div]:bg-background-primary"
      />
    </>
  );
};

const SilenceWarningContent = () => (
  <>
    <AlertTriangle size={20} className="shrink-0 text-validation-destructive" />
    <div className="flex flex-1 flex-col">
      <TypographyP2>
        <FormattedMessage {...dictationIntlMessages.silenceWarningTitle} />
      </TypographyP2>
      <TypographyCaption>
        <FormattedMessage {...dictationIntlMessages.silenceWarningSubtitle} />
      </TypographyCaption>
    </div>
  </>
);

const DictatingContent = () => {
  const dictationMode = useDictationStore((s) => s.dictationMode);
  return (
    <DictationListeningContent
      subtitle={
        dictationMode === 'HANDSFREE' ? (
          <FormattedMessage {...dictationIntlMessages.handsFreeSubtitle} />
        ) : undefined
      }
    />
  );
};

const DictationRhs = memo(({ isSilent }: { isSilent: boolean }) => {
  const { Bars, DesktopBars } = getDictationBubbleUiHost();
  const intl = useIntl();
  const dictationMode = useDictationStore((s) => s.dictationMode);
  const stop = useDictationStore((s) => s.stop);

  return (
    <div className="relative min-h-8 min-w-[44px]">
      {/* Use native audio visualization only when desktop dictation FF is enabled */}
      {getDictationBridgePort().shouldUseNativeAudio() ? (
        <DesktopBars
          isSilent={isSilent}
          className={cn(
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-100 transition-opacity duration-200 ease-out',
            dictationMode === 'HANDSFREE' && 'group-hover:opacity-0'
          )}
        />
      ) : (
        <Bars
          isMic
          isTab={false}
          isSilent={isSilent}
          className={cn(
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-100 transition-opacity duration-200 ease-out',
            dictationMode === 'HANDSFREE' && 'group-hover:opacity-0'
          )}
        />
      )}
      <Button
        size="sm"
        variant={isSilent ? 'destructive' : 'default'}
        iconOnly
        aria-label={intl.formatMessage(dictationIntlMessages.endHandsFree)}
        onClick={() => void stop()}
        className={cn(
          'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-200 ease-out',
          dictationMode === 'HANDSFREE' &&
            'group-hover:pointer-events-auto group-hover:opacity-100'
        )}
      >
        <Square fill="currentColor" />
      </Button>
    </div>
  );
});

DictationRhs.displayName = 'DictationRhs';

export const DictationListening = () => {
  const status = useDictationStore((s) => s.status);
  const hasReceivedTranscript = useDictationStore((s) => s.hasReceivedTranscript);
  const isMobile = useIsMobile();

  // useMaxDuration is called in DictationBubble (not here) so its auto-stop
  // also covers sources whose listening UI is suppressed. Read the atom
  // directly to drive the approaching-warning UI.
  const isMaxDurationApproaching = useAtomValue(isMaxDurationApproachingAtom);

  const { alertType } = useGenericAudioLevelAlert(status === 'LISTENING', false, noop, {
    initialSilenceThresholdMs: 5000,
    silenceThresholdMs: POST_SPEECH_SILENCE_MS,
  });

  const isSilenceWarning = status === 'LISTENING' && alertType !== null;
  const isPostSpeechSilence = isSilenceWarning && hasReceivedTranscript;
  const showSilenceWarningContent = !isPostSpeechSilence && isSilenceWarning;

  const content = useMemo(() => {
    switch (true) {
      case isMaxDurationApproaching:
        return <MaxDurationApproachingContent />;
      case isPostSpeechSilence:
        return <StillDictatingContent />;
      case showSilenceWarningContent:
        return <SilenceWarningContent />;
      default:
        return <DictatingContent />;
    }
  }, [isMaxDurationApproaching, isPostSpeechSilence, showSilenceWarningContent]);

  return (
    <DictationBubbleShell
      animation={bubbleEnterAnimation}
      className={cn(
        'fixed inset-x-0 mx-auto flex h-15 w-87.5 items-center gap-4 overflow-hidden',
        DICTATION_BUBBLE_Z_INDEX_CLASS,
        isMobile ? 'bottom-32' : 'bottom-6',
        showSilenceWarningContent && 'text-validation-destructive'
      )}
    >
      {content}

      <DictationRhs isSilent={showSilenceWarningContent} />
    </DictationBubbleShell>
  );
};
